import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import {
  ADMIN_ROLES,
  FORMATEUR_DASHBOARD_ROLES,
} from '@nzassa/shared';
import { getDefaultRoute } from '@/lib/roleRedirect';

// Layout
import { RootLayout } from '@/components/layout/RootLayout';

// Critical pages — static imports (first paint)
import { LandingPage } from '@/routes/LandingPage';
import { AuthPage } from '@/routes/auth/AuthPage';
import { StudentLoginPage } from '@/routes/auth/StudentLoginPage';
import { AdminLoginPage } from '@/routes/auth/AdminLoginPage';

// Lazy-loaded pages (code-split into separate chunks)
const DashboardPage = lazy(() => import('@/routes/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })));
const PlayerPage = lazy(() => import('@/routes/courses/PlayerPage').then(m => ({ default: m.PlayerPage })));
const LeaderboardPage = lazy(() => import('@/routes/leaderboard/LeaderboardPage').then(m => ({ default: m.LeaderboardPage })));
const ProfilePage = lazy(() => import('@/routes/profile/ProfilePage').then(m => ({ default: m.ProfilePage })));
const VerifyCertificatePage = lazy(() => import('@/routes/certificates/VerifyCertificatePage').then(m => ({ default: m.VerifyCertificatePage })));
const CertificateViewPage = lazy(() => import('@/routes/certificates/CertificateViewPage').then(m => ({ default: m.CertificateViewPage })));
const FormateurDashboard = lazy(() => import('@/routes/formateur/FormateurDashboard').then(m => ({ default: m.FormateurDashboard })));
const ChallengesPage = lazy(() => import('@/routes/challenges/ChallengesPage').then(m => ({ default: m.ChallengesPage })));
const HackathonPage = lazy(() => import('@/routes/hackathon/HackathonPage').then(m => ({ default: m.HackathonPage })));
const AdminPage = lazy(() => import('@/routes/admin/AdminPage').then(m => ({ default: m.AdminPage })));
const AboutPage = lazy(() => import('@/routes/about/AboutPage').then(m => ({ default: m.AboutPage })));
const NotFoundPage = lazy(() => import('@/routes/NotFoundPage').then(m => ({ default: m.NotFoundPage })));

function PageLoader() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4" role="status">
      <img src="/logos/nzassalearn-compact.svg" alt="" className="h-10 w-10 opacity-40" />
      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      <span className="sr-only">Chargement…</span>
    </div>
  );
}

/**
 * Route guard — redirects to /connexion if not authenticated
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
  if (!isAuthenticated) return <Navigate to={`/connexion?redirect=${redirect}`} replace />;
  return <>{children}</>;
}

/**
 * Role-based route guard — redirects to default route if user role not in allowed list
 */
function RoleRoute({ roles, children }: { roles: string[]; children: React.ReactNode }) {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
  if (!isAuthenticated) return <Navigate to={`/connexion?redirect=${redirect}`} replace />;
  if (!user || !roles.includes(user.role)) {
    return <Navigate to={getDefaultRoute(user?.role)} replace />;
  }
  return <>{children}</>;
}

/**
 * Smart root — authenticated users are redirected to their role's default route
 */
function SmartRoot() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const user = useAuthStore(s => s.user);
  if (!isAuthenticated) return <LandingPage />;
  return <Navigate to={getDefaultRoute(user?.role)} replace />;
}

export function App() {
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route element={<RootLayout />}>
        {/* Public */}
        <Route index element={<SmartRoot />} />
        <Route path="auth" element={<AuthPage />} />
        <Route path="connexion" element={<StudentLoginPage />} />
        <Route path="admin-login" element={<AdminLoginPage />} />
        <Route path="a-propos" element={<AboutPage />} />

        {/* Protected — Bénéficiaire */}
        <Route
          path="dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="courses/:courseId"
          element={
            <ProtectedRoute>
              <PlayerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="leaderboard"
          element={
            <ProtectedRoute>
              <LeaderboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Certificate view (auth required) */}
        <Route
          path="certificates/:id"
          element={
            <ProtectedRoute>
              <CertificateViewPage />
            </ProtectedRoute>
          }
        />

        {/* Formateur Dashboard */}
        <Route
          path="formateur/*"
          element={
            <RoleRoute roles={FORMATEUR_DASHBOARD_ROLES}>
              <FormateurDashboard />
            </RoleRoute>
          }
        />

        {/* Challenges — accessible to all authenticated users */}
        <Route
          path="challenges"
          element={
            <ProtectedRoute>
              <ChallengesPage />
            </ProtectedRoute>
          }
        />

        {/* Hackathon — accessible to all authenticated users */}
        <Route
          path="hackathon"
          element={
            <ProtectedRoute>
              <HackathonPage />
            </ProtectedRoute>
          }
        />

        {/* Admin Dashboard */}
        <Route
          path="admin/*"
          element={
            <RoleRoute roles={ADMIN_ROLES}>
              <AdminPage />
            </RoleRoute>
          }
        />

        {/* Certificate verification (public) */}
        <Route path="verify/:code" element={<VerifyCertificatePage />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
    </Suspense>
  );
}
